/** Schnittstelle der Plate-Module (die Module selbst sind ungeprüft, siehe PlateMarkdownEditor.tsx). */
import type { SectionContent } from "@the-real-insight/tri-model";
import type { SuggestionSummary } from "./plateValue";

export interface PlateMarkdownEditorLabels {
  editor: string;
  bold: string;
  italic: string;
  heading2: string;
  heading3: string;
  quote: string;
  bulletList: string;
  numberedList: string;
  table: string;
  tableRow: string;
  tableColumn: string;
  deleteRow: string;
  deleteColumn: string;
  comment: string;
  /** Gemeinsames Bearbeiten: verbunden, Verbindung wird aufgebaut, „Ebenfalls hier:“. */
  live: string;
  connecting: string;
  alsoHere: string;
  image: string;
  deleteImage: string;
  suggestingBadge: string;
  /** Fußnote einfügen; Überschrift und Platzhalter der Fußnotenfelder unter dem Editor. */
  footnote: string;
  footnotes: string;
  footnotePlaceholder: string;
  /** Formeleditor (TeX). */
  formula: string;
  formulaInsertTitle: string;
  formulaEditTitle: string;
  formulaIntro: string;
  formulaTex: string;
  formulaDisplay: string;
  formulaLabel: string;
  formulaPreview: string;
  formulaEmpty: string;
  formulaSave: string;
  formulaRemove: string;
  formulaCancel: string;
  formulaCheat: string;
  formulaCheatHint: string;
  formulaCheatBasics: string;
  formulaCheatRelations: string;
  formulaCheatGreek: string;
  formulaCheatOperators: string;
  formulaCheatUnits: string;
  formulaCheatStructures: string;
  formulaCheatExamples: string;
}

/** Was die Seite mit dem Editor tun kann, sobald er bereit ist. */
/** Ein Begriff aus dem Glossar, gezogen oder per Klick: Text und Verweis auf seine Definition (ohne: nur Text). */
export interface TermInsert {
  text: string;
  href?: string | null;
}

/** Datentyp beim Ziehen eines Begriffs in den Editor (JSON mit TermInsert). */
export const TERM_DRAG_TYPE = "application/x-tri-term";

export interface PlateEditorHandle {
  /** Begriff an der Einfügemarke einsetzen — als Verweis, im Vorschlagsmodus als Vorschlag. */
  insertTerm: (term: TermInsert) => void;
  /** Markdown ohne offene Vorschläge (als wären alle abgelehnt) und ohne Kommentarmarken. */
  getMarkdown: () => string;
  /** Abschnitt mit Blockmodell: die Blöcke ohne offene Vorschläge; sonst null. */
  getBlocks: () => SectionContent | null;
  /** Der Plate-Inhalt mit allen Marken — wird mitgespeichert. */
  getValue: () => unknown[];
  getSuggestions: () => SuggestionSummary[];
  /** Kennung des Blocks, in dem die Auswahl beginnt (Anker eines neuen Kommentars). */
  selectionElement: () => string | null;
  /** Fehlende Marken offener Fäden über ihren Anker (Element + Zitat) neu setzen. */
  ensureCommentMarks: (anchors: { markId: string; element: string | null; quote: string }[]) => void;
  /** Markiert die Auswahl als Kommentar `id`; liefert den markierten Text, ohne Auswahl null. */
  addComment: (id: string) => string | null;
  removeComment: (id: string) => void;
  accept: (suggestionId: string) => void;
  reject: (suggestionId: string) => void;
}

export interface PlateMarkdownEditorProps {
  /** Ausgangstext als Markdown (GFM). */
  markdown: string;
  /** Gespeicherter Plate-Inhalt mit Kommentaren und Vorschlägen; hat Vorrang vor `blocks` und `markdown`. */
  value?: unknown[] | null;
  /** Inhalt als Blockmodell — dann wird in Blöcken gespeichert, nicht in Markdown. */
  blocks?: SectionContent | null;
  labels: PlateMarkdownEditorLabels;
  currentUserId: string;
  /** Direkt bearbeiten (Autor, Leitung); sonst nur vorschlagen. */
  canEdit: boolean;
  /** Vorschlagsmodus auch für den, der direkt bearbeiten darf. */
  suggesting: boolean;
  onReady: (handle: PlateEditorHandle) => void;
  onChange?: () => void;
  onCommentClick?: (discussionId: string) => void;
  /** Knopf „Kommentar“ — die Seite fragt nach dem Text und ruft dann `addComment`. */
  onCommentRequest?: () => void;
  /**
   * Gemeinsames Bearbeiten über Hocuspocus: Dokumentname, Adresse, Zugriffstoken (als Funktion,
   * damit ein erneuertes Token genommen wird) und wie man selbst bei den anderen erscheint.
   */
  collab?: { document: string; url: string; token: () => string | Promise<string>; user: { name: string; color: string }; onMessage?: (message: { type: string }) => void } | null;
  /** Bild hochladen; liefert die Adresse für den Text. Fehlt sie, gibt es keine Bilder einzufügen. */
  onImageUpload?: (file: File) => Promise<{ url: string; name: string }>;
}

export interface PlateMarkdownViewProps {
  markdown: string;
  value?: unknown[] | null;
  blocks?: SectionContent | null;
  /** Das gezeichnete Wurzelelement — die Seite legt dort Anforderungen und Belegstellen als Markierung darüber. */
  onRendered: (root: HTMLElement) => void;
  onCommentClick?: (discussionId: string) => void;
}
